import ContentTypes from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import StoryFrameworkPlayer from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/story/framework/player/StoryFrameworkPlayer";
import StoryFrameworkPlayerEvent from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/story/framework/player/StoryFrameworkPlayerEvent";
import StoryFrameworkPlayerFactory from "../../../../../../shared/lib/html5-software-library/src/ranj/eng/story/framework/player/StoryFrameworkPlayerFactory";
import GameEvent from "../../../../../../shared/src/ranj/app/core/GameEvent";
import SaveEvent from "../../../../../../shared/src/ranj/equalture/savegame/SaveEvent";
import GameFlowEvent from "../../core/controller/GameFlowEvent";
import AbstractScenarioPlayer from "./AbstractScenarioPlayer";
import ScenarioPlayerEvent from "./ScenarioPlayerEvent";

/**
 * NGTScenarioPlayer.js
 * 
 * Created on 26-sep-2013, 13:35:14
 *
 * @author Ivo Swartjes for Ranj
 **/
export default class NGTScenarioPlayer extends AbstractScenarioPlayer {
    constructor(dispatcher, variableManager, gameClock) {
        super();
        this.eventDispatcher = dispatcher;

        this._variableManager = variableManager;
        this._gameClock = gameClock;

        /**@type {StoryFrameworkPlayer} */
        this._storyPlayer;
        this._storyEvaluationTimer;

        this._scenario;
        this._contentNodeMap = {};
    }

    init(scenario, serializedStoryMemento) {
        this._scenario = scenario;
        this._storyPlayer = StoryFrameworkPlayerFactory.createStoryFrameworkPlayer(this._scenario.story, this._gameClock, this._variableManager.solve.bind(this._variableManager));
        // Use story memento if provided
        if (serializedStoryMemento) {
            var storyMemento = StoryFrameworkPlayerFactory.createStoryFrameworkPlayerMemento();
            storyMemento.deserialize(serializedStoryMemento);
            this._storyPlayer.load(storyMemento);

            this._storyPlayer.availableStoryNodes().array.forEach(node => {
                const content = node.getData();
                if (content != null) {
                    this._contentNodeMap[content.getLabel()] = node;
                }
            });
        }
    }

    getScenario() {
        return this._scenario;
    }

    getStoryPlayer() {
        return this._storyPlayer;
    }

    play() {
        this.addEventListeners();

        this._storyEvaluationTimer = setInterval(this.handleStoryEvaluationTimer.bind(this), 100);
        this._gameClock.start();
    }

    setPaused(value) {
        if (value === true) {
            clearInterval(this._storyEvaluationTimer);
            this._gameClock.pause();
        } else {
            this._gameClock.start();
        }
    }

    stop() {
        if (this._storyPlayer === null)
            return;

        clearInterval(this._storyEvaluationTimer);
        this._gameClock.stop();

        this.removeEventListeners();
    }

    getStoryNodeByContentLabel(contentLabel) {
        return this._contentNodeMap[contentLabel];
    }

    contentCompleted(content) {
        console.info("NGTScenarioPlayer: Content completed");
        var node = this._contentNodeMap[content.getLabel()];
        if (node) {
            this._storyPlayer.complete(node);
            delete this._contentNodeMap[content.getLabel()];
        }
        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.CONTENT_COMPLETED, content, this);
        this.eventDispatcher.dispatch(SaveEvent.SAVE);
        this._storyPlayer.evaluate();
    }

    // ------------------------------------------------------------
    // Protected functions
    // ------------------------------------------------------------
    dispatchScenarioPlayerEvent(type, content, contentPlayer) {
        this.eventDispatcher.dispatch(type, {
            data:
            {
                content: content,
                contentPlayer: contentPlayer
            }
        });
    }

    addEventListeners() {
        this._storyPlayer.addEventListener(StoryFrameworkPlayerEvent.STORY_NODE_AVAILABLE, this.handleStoryNodeAvailable, this);
        this._storyPlayer.addEventListener(StoryFrameworkPlayerEvent.STORY_NODE_UNAVAILABLE, this.handleStoryNodeUnavailable, this);
        this._storyPlayer.addEventListener(StoryFrameworkPlayerEvent.STORY_END, this.handleStoryEnd, this);

        // TODO: listen to pause / resume game events.
    }

    removeEventListeners() {
        this._storyPlayer.removeEventListener(StoryFrameworkPlayerEvent.STORY_NODE_AVAILABLE, this.handleStoryNodeAvailable, this);
        this._storyPlayer.removeEventListener(StoryFrameworkPlayerEvent.STORY_NODE_UNAVAILABLE, this.handleStoryNodeUnavailable, this);
        this._storyPlayer.removeEventListener(StoryFrameworkPlayerEvent.STORY_END, this.handleStoryEnd, this);
    }

    handleStoryEvaluationTimer() {
        this._storyPlayer.evaluate();

        // Send out a game time tick for timelines etc.
        this.eventDispatcher.dispatch(GameEvent.TIME_CHANGE, { data: { time: this._gameClock.getCurrentTime() } });
    }

    handleStoryNodeAvailable(event) {
        var content = event.data.storyNode.getData();
        this._contentNodeMap[content.getLabel()] = event.data.storyNode;

        // TODO: check if content is attached to a known key moment; if so, advance the key moment model.

        // Decide what the availability of this story node means for the game
        switch (content.getType()) {
            case ContentTypes.TYPE_DIALOG:
                if (content.getPlayerInitiated()) {
                    // Pull dialog (needs to be initiated by player)
                    if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PULL_DIALOG_AVAILABLE)) {
                        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PULL_DIALOG_AVAILABLE, content, this);
                    } else {
                        this.ignoreContent(content);
                    }
                } else {
                    // Push dialog (typically played immediately)
                    if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PUSH_DIALOG_AVAILABLE)) {
                        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PUSH_DIALOG_AVAILABLE, content, this);
                    } else {
                        this.ignoreContent(content);
                    }
                }
                break;

            case ContentTypes.TYPE_DECISION:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.DECISION_DIALOG_AVAIABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.DECISION_DIALOG_AVAIABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            // TODO: quiz content

            case ContentTypes.TYPE_MOVIE:
                console.info("NGTScenarioPlayer: Movie available: " + event.data.storyNode.id);
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.MOVIE_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.MOVIE_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_SET_VARIABLE:
                this.handleSetVariableContent(content);
                break;

            // Workspace content    
            case ContentTypes.TYPE_FILE:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.FILE_CONTENT_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.FILE_CONTENT_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            // TODO: NPC
            case ContentTypes.TYPE_NPC:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.NPC_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.NPC_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            // TODO: Narrative event

            case ContentTypes.TYPE_TEXT:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.TEXT_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.TEXT_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            default:
                this.ignoreContent(content);
                break;
        }
    }

    handleStoryNodeUnavailable(event) {
        var content = event.data.storyNode.getData();
        delete this._contentNodeMap[content.getLabel()];

        switch (content.getType()) {
            case ContentTypes.TYPE_DIALOG:
                if (content.getPlayerInitiated()) {
                    // Pull dialog: let the game know.
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PULL_DIALOG_UNAVAILABLE, content, this);
                }
                break;
        }
    }

    handleSetVariableContent(content) {
        var variableMutations = content.getVariableMutations();
        let complete = true;
        for (var i = 0; i < variableMutations.length; i++) {
            var mut = variableMutations[i];
            switch (mut.variableName) {
                case "transitionToDialog": {
                    complete = false;
                    this.eventDispatcher.dispatch(GameFlowEvent.TRANSITION_TO_DIALOG, { dialogIndex: mut.value, content: content });
                    break;
                }
                default:
                    this._variableManager.setVariable(mut.variableName, mut.value, mut.mutationType);
            }
        }
        if (complete) {
            this.contentCompleted(content);
        }
    }

    handleSetNumberContent(content) {
        this._variableManager.setNumber(content.name, content.value, content.operator);
        this.contentCompleted(content);
    }

    handleStoryEnd(event) {
        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.SCENARIO_END);
    }

    ignoreContent(content) {
        console.warn("WARNING: content type " + content.getType() + " not handled by game!");
        this.contentCompleted(content);
    }

}
